import { bookingActions, isEditing } from '../data/models/Booking';
import { isPastBooking } from '../data/models/obeeBooking';
import { isEmpty, sortListByProperty, unique } from './helpers';
import { safeParse } from './stringHelpers';

/**
 * Get the terms for any selected experience to override the default terms
 * return null if no experiences or no terms
 * @param booking
 */
export const getExperienceTerms = (booking) => {
  const customTerms = booking?.selectedExperiences?.find(
    (experience) => !isEmpty(experience?.experience?.termsAndConditions),
  )?.experience?.termsAndConditions?.[0];

  return customTerms;
};

/**
 * With a list of vouchers, calculate which ones get used and how much is used for each
 */
export const getVouchersWithTotals = (voucherReferences, totalCost) => {
  let remainingTotal = totalCost;

  // Use up the vouchers with the least remaining first
  const vouchersWithTotals = sortListByProperty(voucherReferences, 'amount')
    .filter((voucher) => voucher.valid)
    .map((voucherReference) => {
      let newVoucherAmount = voucherReference.amount;

      // Check we can actually use this voucher
      if (remainingTotal > 0) {
        // if we can use all of the voucher, do it
        if (remainingTotal > voucherReference.amount) {
          newVoucherAmount = 0;
          remainingTotal -= voucherReference.amount;
        } else {
          // Otherwise, use as much of the voucher as possible
          newVoucherAmount -= remainingTotal;
          remainingTotal = 0;
        }
      }

      return {
        ...voucherReference,
        originalAmount: voucherReference.amount,
        remainingAmount: newVoucherAmount,
        usedAmount: voucherReference.amount - newVoucherAmount,
      };
    });

  return vouchersWithTotals;
};

/**
 * Get the addon groups that should show before and after the details screen
 * @param booking
 * @param addonGroups
 * @returns {*}
 */
export const getAddonGroupsForBooking = (booking, addonGroups) => {
  const addonsForExperiences = booking?.selectedExperiences
    .filter((experienceCount) => experienceCount?.count > 0)
    .reduce((accum, experienceCount) => {
      const showBeforeDetails =
        experienceCount?.experience?.showBeforeDetails ||
        experienceCount?.experience?.experienceTypeSettings?.accommodation;

      const experienceTypeSettings = experienceCount?.experience?.experienceTypeSettings;

      const addonsForExperience = (experienceCount?.experience?.addonGroups ?? []).map(
        (addonGroup) => ({
          ...addonGroup,
          showBeforeDetails: showBeforeDetails || !!addonGroup?.showBeforeDetails,
          experienceTypeSettings,
          experienceId: experienceCount?.experience?.id,
        }),
      );
      return [...accum, ...addonsForExperience];
    }, []);
  const genericAddons = addonGroups?.data ?? [];

  // There might be some overlap with addons - always prioritise the addons for the experience
  const allAddons = unique([...addonsForExperiences, ...genericAddons], 'id');

  // filter our any addons that aren't meant to show (e.g. only show before details)
  return {
    beforeDetails: allAddons.filter((addon) => addon?.showBeforeDetails),
    afterDetails: allAddons.filter((addon) => !addon?.showBeforeDetails),
    all: allAddons,
  };

  // return chosenExperience?.addonGroups.filter(
  //   (addonGroup) =>
  //     (!addonGroup.min || addonGroup.min <= booking.guests) &&
  //     (!addonGroup.max || addonGroup.max >= booking.guests),
  //
  //   // TODO check day is in valid days of week
  //   // TODO check date is in date range
  // );
};

export const bookingCanBeEdited = (obeeBooking, action) => {
  if (!isPastBooking(obeeBooking.data) && obeeBooking?.data?.bookingCutOffs) {
    const cutoffs = safeParse(obeeBooking?.data?.bookingCutOffs);

    // If editing cc fields, it doesn't matter whether the rest of the booking can be edited
    if (cutoffs?.canEditWithPayment === true && action === bookingActions.cc) {
      return true;
    }

    // If restaurant disallows editing/cancelling
    if (!cutoffs?.canEditOnline) {
      return false;
    }

    // If restaurant disallows editing when a credit card has been provided
    if (cutoffs?.canEditWithPayment === false) {
      return false;
    }

    // If the current time is beyond the cutoff for the booking
    if (Date.now() / 1000 > cutoffs?.lastOnlineCancellableSeconds) {
      return false;
    }
  }

  return true;
};

export function convertAddonsData(addons) {
  // Check if input is an array
  if (!Array.isArray(addons)) {
    return null;
  }

  const result = {};

  addons.forEach((addon) => {
    if (isEmpty(addon?.id)) {
      return;
    }

    result[addon.id] = {
      addon: {
        ...addon,
        id: addon.id,
        price: parseFloat(addon.claimedSinglePrice), // Convert price to a number
        count: addon?.claimedQuantity,
      },
      count: addon?.claimedQuantity,
      compulsory: addon?.compulsory,
      groupIndex: 0,
      addonGroupId: addon?.addonGroupId,
    };
  });

  return result;
}
